import { createForm, Flex, Heading, Spinner, Textarea, TextInput } from '@applyboard/crystal-ui';
import { useProgramDetails } from '../context/ProgramDetailsContext';
import styled from '@emotion/styled';
import { ErrorHandler } from '~/pages/ErrorPage';

type ProgramFormFields = {
  code: string;
  name: string;
  description: string;
  programLevelName: string;
  fieldOfStudyName: string;
  programLength: string;
  lengthUnit: string;
  isLanguageProgram: string;
  url: string;
  deliveryMethod: string;
  isPublished: string;
};

export function ProgramInformation() {
  const { program, isLoadingProgram, isProgramError } = useProgramDetails();

  if (!isProgramError && isLoadingProgram) {
    return (
      <Flex p={5} gap={5} align="center" direction="column" role="progressbar">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isProgramError || !program) {
    return <ErrorHandler error={isProgramError} customMessage="Failed to fetch the program details." />;
  }

  const { Form, Field } = createForm<ProgramFormFields>();

  const formDefaultValues: ProgramFormFields = {
    code: program.code,
    name: program.name,
    description: program.description,
    programLevelName: program.programLevelName as string,
    fieldOfStudyName: program.fieldOfStudyName as string,
    programLength: program.length.toString(),
    lengthUnit: program.lengthUnit,
    isLanguageProgram: program.isLanguageProgram ? 'Yes' : 'No',
    url: program.url,
    deliveryMethod: formatDeliveryMethod(program.deliveryMethod),
    isPublished: program.isPublished ? 'Published' : 'Unpublished',
  };

  return (
    <Flex direction="column" gap={8}>
      <Heading contrast="high" level={2} variant="titleS">
        Program Information
      </Heading>
      {/* TO-DO: onSubmit to be implemented when we have a way to edit programs */}
      <FormWrapper>
        <Form defaultValues={formDefaultValues} onSubmit={() => {}}>
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="code" label="Program Code" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="name" label="Program Name" disabled />
            </Flex.Item>
            <Flex.Item basis="100%">
              <Field as={Textarea} name="description" label="Program Description" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="programLevelName" label="Program Level" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="fieldOfStudyName" label="Field of Study" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="programLength" label={`Length (${program?.lengthUnit})`} disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="isLanguageProgram" label="Language Program" disabled />
            </Flex.Item>
            <Flex.Item basis="100%">
              <Field as={TextInput} name="url" label="URL" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="deliveryMethod" label="Delivery Method" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="isPublished" label="Publication Status" disabled />
            </Flex.Item>
          </Flex>
        </Form>
      </FormWrapper>
    </Flex>
  );
}

function formatDeliveryMethod(deliveryMethod: string) {
  return deliveryMethod
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const FormWrapper = styled.div`
  width: 100%;
  max-width: 800px;
`;
